"use client";

import React, { useState, useEffect } from "react";
import { Timer, Bell, Lock, Zap, ArrowRight, CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState({
    hours: 14,
    minutes: 45,
    seconds: 22
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return {
            ...prev,
            seconds: prev.seconds - 1
          };
        } else if (prev.minutes > 0) {
          return {
            ...prev,
            minutes: prev.minutes - 1,
            seconds: 59
          };
        } else if (prev.hours > 0) {
          return {
            ...prev,
            hours: prev.hours - 1,
            minutes: 59,
            seconds: 59
          };
        }
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const formatTime = (val: number) => val.toString().padStart(2, "0");
  return <div className="flex items-center gap-2 sm:gap-4 font-mono text-3xl sm:text-5xl md:text-6xl font-bold tracking-tighter text-primary animate-pulse">
      <div className="flex flex-col items-center">
        <span>{formatTime(timeLeft.hours)}</span>
        <span className="text-xs sm:text-sm font-sans font-medium text-muted-foreground tracking-normal mt-1">HRS</span>
      </div>
      <span className="pb-6">:</span>
      <div className="flex flex-col items-center">
        <span>{formatTime(timeLeft.minutes)}</span>
        <span className="text-xs sm:text-sm font-sans font-medium text-muted-foreground tracking-normal mt-1">MIN</span>
      </div>
      <span className="pb-6">:</span>
      <div className="flex flex-col items-center">
        <span>{formatTime(timeLeft.seconds)}</span>
        <span className="text-xs sm:text-sm font-sans font-medium text-muted-foreground tracking-normal mt-1">SEC</span>
      </div>
    </div>;
}
function DropSignupForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="h-full flex flex-col items-center justify-center text-center p-8 bg-card/50 border border-primary/20 rounded-xl backdrop-blur-sm animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-2">You're on the list!</h3>
        <p className="text-muted-foreground mb-6 max-w-xs mx-auto">{successMessage}</p>
        <Button variant="outline" className="w-full border-primary/30 hover:bg-primary/10 text-primary" onClick={() => window.location.reload()}>
          Register Another
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="name" rules={{
        required: false
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground">Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="bg-background/50 border-border/50 focus:border-primary/50 focus:ring-primary/20 transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground">Email <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="bg-background/50 border-border/50 focus:border-primary/50 focus:ring-primary/20 transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="phone" rules={{
        required: false
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground">Phone (Optional)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 555-123-4567" {...field} className="bg-background/50 border-border/50 focus:border-primary/50 focus:ring-primary/20 transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="size" rules={{
        required: false
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground">Size Preference</FormLabel>
              <FormControl>
                <div className="relative">
                  <select {...field} className="flex h-9 w-full rounded-md border border-border/50 bg-background/50 px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50 text-foreground appearance-none">
                    <option value="" disabled selected>Select size</option>
                    {["6", "6.5", "7", "7.5", "8", "8.5", "9", "9.5", "10", "10.5", "11", "11.5", "12"].map(size => <option key={size} value={size} className="bg-card text-foreground">
                        US {size}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                    <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-bold tracking-wide uppercase bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_-5px_var(--color-primary)] transition-all duration-300">
        {isSubmitting ? <span className="flex items-center gap-2">
            <span className="animate-spin"></span> Processing...
          </span> : <span className="flex items-center gap-2">
            <Lock className="size-4" /> Secure Access
          </span>}
      </Button>
      
      <p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5">
        <Lock className="size-3" /> 
        Encrypted & Secure. No spam, just drops.
      </p>
    </Form>;
}
export default function ActiveDropCountdown() {
  return <section id="active-drop-countdown" className="dark bg-background text-foreground relative overflow-hidden py-16 lg:py-24">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-background to-background z-0 pointer-events-none" />
      <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1564626/img/metal_texture_pattern.png')] opacity-[0.03] mix-blend-overlay z-0 pointer-events-none" />
      
      {}
      <div className="container relative z-10 mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {}
          <div className="lg:col-span-7 flex flex-col gap-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-bold uppercase tracking-widest animate-pulse">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                </span>
                Incoming Drop
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-white uppercase leading-[0.9]">
                Phantom <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-emerald-400">Velocity</span> X1
              </h2>
              
              <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">
                Limited edition release. Engineered for zero-gravity performance. 
                Secure your spot in the queue before the public release window opens.
              </p>
            </div>

            <div className="p-6 sm:p-8 rounded-2xl bg-card/30 border border-white/5 backdrop-blur-sm shadow-2xl">
              <div className="flex items-center gap-3 mb-6 text-muted-foreground text-sm uppercase tracking-widest font-semibold">
                <Timer className="size-4 text-primary" />
                <span>Drop Goes Live In</span>
              </div>
              <CountdownTimer />
            </div>

            {}
            <div className="space-y-3 max-w-md">
              <div className="flex justify-between items-end">
                <span className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <Zap className="size-4 text-yellow-400 fill-yellow-400" />
                  Hype Level
                </span>
                <span className="text-primary font-mono font-bold">98%</span>
              </div>
              <Progress value={98} className="h-3 bg-secondary/20" />
              <p className="text-xs text-muted-foreground text-right">
                High demand expected. Waitlist filling fast.
              </p>
            </div>
          </div>

          {}
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl bg-card border border-border shadow-2xl overflow-hidden">
              {}
              <div className="h-1 w-full bg-gradient-to-r from-primary via-white to-primary" />
              
              <div className="p-6 sm:p-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">Get Early Access</h3>
                  <p className="text-muted-foreground text-sm">
                    Join the priority list to receive a secure purchase link 1 hour before the public drop.
                  </p>
                </div>

                <FormProvider formId="drop_countdown_form_home" sectionName="active-drop-countdown" defaultValues={{
                name: "",
                email: "",
                phone: "",
                size: ""
              }} validationRules={{
                email: {
                  required: true,
                  pattern: "email"
                }
              }} submitText="Secure Access" successMessage="You've been added to the priority list. Watch your inbox!">
                  <DropSignupForm />
                </FormProvider>
              </div>
              
              {}
              <div className="bg-muted/30 p-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span>Next Drop: <strong>Friday, 10:00 AM EST</strong></span>
                <Link to="/limited-drops" className="flex items-center gap-1 hover:text-primary transition-colors">
                  View Calendar <ArrowRight className="size-3" />
                </Link>
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </section>;
}